import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MobileLayout from "../../components/MobileLayout";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import { formatIndianCurrency } from "../../utils/formatters";
import {
  RefreshCw,
  Download,
  TrendingUp,
  Calendar,
  AlertCircle,
  Crown,
  Star,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const DailySalesReport = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
  });

  const [totals, setTotals] = useState({
    totalSales: 0,
    totalCost: 0,
    totalGrossProfit: 0,
    totalNetProfit: 0,
    totalGSTSales: 0,
    totalGSTCost: 0,
    totalGSTProfit: 0,
  });

  const [planTypeStats, setPlanTypeStats] = useState({
    prime: 0,
    premium: 0,
  });

  // Overall summary (without date filter)
  const [overallSummary, setOverallSummary] = useState({
    totalSales: 0,
    totalCost: 0,
    totalGrossProfit: 0,
    totalNetProfit: 0,
    totalGstSales: 0,
    totalGstCost: 0,
    totalGstProfit: 0,
    count: 0,
    planTypeBreakdown: {},
  });

  useEffect(() => {
    fetchEarnings();
    fetchSummary();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.fromDate || filters.toDate) {
        fetchEarnings();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.fromDate, filters.toDate]);

  const fetchEarnings = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params = {};
      if (filters.fromDate) params.from_date = filters.fromDate;
      if (filters.toDate) params.to_date = filters.toDate;

      const res = await api.get("/my-earnings", { params });

      const data = res?.data || {};
      console.log("Earnings data:", data);

      if (data.success) {
        setDailyData(data.data || []);
        setTotals({
          totalSales: data.totals?.totalSales || 0,
          totalCost: data.totals?.totalCost || 0,
          totalGrossProfit: data.totals?.totalGrossProfit || 0,
          totalNetProfit: data.totals?.totalNetProfit || 0,
          totalGSTSales: data.totals?.totalGSTSales || 0,
          totalGSTCost: data.totals?.totalGSTCost || 0,
          totalGSTProfit: data.totals?.totalGSTProfit || 0,
        });
        setPlanTypeStats(data.planTypeStats || { prime: 0, premium: 0 });
      } else {
        setDailyData([]);
      }

      setLastUpdate(new Date());
    } catch (err) {
      console.error("Failed to fetch earnings:", err);
      toast.error("Failed to load earnings data");
      setDailyData([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await api.get("/summary");
      const data = res?.data || {};

      if (data.success) {
        setOverallSummary(data.data || {});
      }
    } catch (err) {
      console.error("Failed to fetch summary:", err);
    }
  };

  const handleRefresh = () => {
    fetchEarnings(true);
    fetchSummary();
  };

  const handleDateChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({ fromDate: "", toDate: "" });
    fetchEarnings();
  };

  // Helper function to render plan badge (inline, smaller)
  const renderPlanBadge = (planType) => {
    const isPremium = planType?.toLowerCase() === "premium";

    return (
      <span
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ml-2 ${
          isPremium
            ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
            : "bg-blue-500 text-white"
        }`}
      >
        {isPremium ? (
          <Crown className="w-2.5 h-2.5" />
        ) : (
          <Star className="w-2.5 h-2.5" />
        )}
        {isPremium ? "P" : "R"}
      </span>
    );
  };

  const exportToCSV = () => {
    if (dailyData.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      const headers = [
        "Date",
        "Plan Type",
        "Sales",
        "Cost",
        "Gross Profit",
        "Net Profit",
        "GST (Sales)",
        "GST (Cost)",
        "GST (Profit)",
      ];

      const rows = dailyData.map((day) => [
        day.date,
        day.planType || "prime",
        day.sales.toFixed(2),
        day.cost.toFixed(2),
        day.grossProfit.toFixed(2),
        day.netProfit.toFixed(2),
        day.gst?.sales?.toFixed(2) || "0.00",
        day.gst?.cost?.toFixed(2) || "0.00",
        day.gst?.profit?.toFixed(2) || "0.00",
      ]);

      rows.push([
        "TOTAL",
        "",
        totals.totalSales.toFixed(2),
        totals.totalCost.toFixed(2),
        totals.totalGrossProfit.toFixed(2),
        totals.totalNetProfit.toFixed(2),
        totals.totalGSTSales.toFixed(2),
        totals.totalGSTCost.toFixed(2),
        totals.totalGSTProfit.toFixed(2),
      ]);

      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `daily-sales-report-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success("Report exported successfully");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Failed to export report");
    }
  };

  if (loading) {
    return (
      <MobileLayout showBottomNav={false}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout showBottomNav={false}>
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <div className="p-4 space-y-4">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-primary font-medium text-sm mb-2"
          >
            ← Back
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-bold text-gray-900">
                Daily Sales Report
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Track your daily sales & profit performance
              </p>
            </div>
            <button
              onClick={exportToCSV}
              disabled={dailyData.length === 0}
              className="p-2 bg-emerald-600 text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>
          {lastUpdate && (
            <p className="text-xs text-gray-500 mt-2">
              Last updated:{" "}
              {lastUpdate.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          )}
        </div>

        {/* No Active Deposit Warning */}
        {dailyData.length === 0 && !loading && (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-yellow-700 text-sm">
                  No Earnings Yet
                </h3>
                <p className="text-xs text-gray-700 mt-1">
                  Earnings are calculated daily at midnight for active deposits.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Overall Summary Card (Lifetime Stats) */}
        {overallSummary.count > 0 && (
          <div className="bg-gradient-to-br from-primary to-primary-hover rounded-xl p-4 text-white shadow-lg">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Lifetime Summary ({overallSummary.count} days)
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <p className="text-xs opacity-80 mb-1">Total Sales</p>
                <p className="text-lg font-mono font-bold">
                  {formatIndianCurrency(overallSummary.totalSales)}
                </p>
              </div>
              <div>
                <p className="text-xs opacity-80 mb-1">Total Profit</p>
                <p className="text-lg font-mono font-bold">
                  {formatIndianCurrency(overallSummary.totalNetProfit)}
                </p>
              </div>
              <div>
                <p className="text-xs opacity-80 mb-1">Total Cost</p>
                <p className="text-sm font-mono">
                  {formatIndianCurrency(overallSummary.totalCost)}
                </p>
              </div>
              <div>
                <p className="text-xs opacity-80 mb-1">Total GST</p>
                <p className="text-sm font-mono">
                  {formatIndianCurrency(overallSummary.totalGstProfit)}
                </p>
              </div>
            </div>

            {/* Plan Type Breakdown */}
            {overallSummary.planTypeBreakdown &&
              Object.keys(overallSummary.planTypeBreakdown).length > 0 && (
                <div className="border-t border-white/20 pt-3">
                  <p className="text-xs opacity-80 mb-2">Plan Type Breakdown</p>
                  <div className="grid grid-cols-2 gap-2">
                    {overallSummary.planTypeBreakdown.prime && (
                      <div className="bg-white/10 rounded-lg p-2">
                        <div className="flex items-center gap-1 mb-1">
                          <Star className="w-3 h-3" />
                          <span className="text-xs font-semibold">Prime</span>
                        </div>
                        <p className="text-xs opacity-80">
                          {overallSummary.planTypeBreakdown.prime.count} days
                        </p>
                        <p className="text-sm font-mono font-bold">
                          {formatIndianCurrency(
                            overallSummary.planTypeBreakdown.prime
                              .totalNetProfit
                          )}
                        </p>
                      </div>
                    )}
                    {overallSummary.planTypeBreakdown.premium && (
                      <div className="bg-white/10 rounded-lg p-2">
                        <div className="flex items-center gap-1 mb-1">
                          <Crown className="w-3 h-3" />
                          <span className="text-xs font-semibold">Premium</span>
                        </div>
                        <p className="text-xs opacity-80">
                          {overallSummary.planTypeBreakdown.premium.count} days
                        </p>
                        <p className="text-sm font-mono font-bold">
                          {formatIndianCurrency(
                            overallSummary.planTypeBreakdown.premium
                              .totalNetProfit
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>
        )}

        {/* Plan Type Stats for Filtered Data */}
        {(planTypeStats.prime > 0 || planTypeStats.premium > 0) && (
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-3 text-sm">
              {filters.fromDate || filters.toDate
                ? "Filtered Plan Distribution"
                : "Plan Distribution"}
            </h3>
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-gray-600">Prime:</span>
                <span className="text-sm font-bold text-gray-900">
                  {planTypeStats.prime} days
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-purple-600" />
                <span className="text-sm text-gray-600">Premium:</span>
                <span className="text-sm font-bold text-gray-900">
                  {planTypeStats.premium} days
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Filtered Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">
                  {filters.fromDate || filters.toDate ? "Filtered" : "Total"}{" "}
                  Sales
                </p>
                <p className="text-lg font-mono font-bold text-primary">
                  {formatIndianCurrency(totals.totalSales)}
                </p>
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">
                  {filters.fromDate || filters.toDate ? "Filtered" : "Total"}{" "}
                  Profit
                </p>
                <p className="text-lg font-mono font-bold text-emerald-600">
                  {formatIndianCurrency(totals.totalNetProfit)}
                </p>
              </div>
              <TrendingUp className="w-5 h-5 text-blue-500" />
            </div>
          </div>
        </div>

        {/* Additional Summary */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-3">
            {filters.fromDate || filters.toDate
              ? "Filtered Summary"
              : "Summary"}
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Total Cost</p>
              <p className="text-sm font-mono font-bold text-gray-900">
                {formatIndianCurrency(totals.totalCost)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Gross Profit</p>
              <p className="text-sm font-mono font-bold text-gray-900">
                {formatIndianCurrency(totals.totalGrossProfit)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Total GST</p>
              <p className="text-sm font-mono font-bold text-gray-900">
                {formatIndianCurrency(totals.totalGSTProfit)}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <h3 className="font-semibold text-gray-900 text-sm">
                Date Filter
              </h3>
            </div>
            <button
              onClick={resetFilters}
              className="text-xs text-primary font-medium"
            >
              Reset
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleDateChange("fromDate", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => handleDateChange("toDate", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
        </div>

        {/* Daily Data Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700">
                    Date
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700">
                    Sales
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700">
                    Cost
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700">
                    Gross Profit
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700">
                    Net Profit
                  </th>
                </tr>
              </thead>
              <tbody>
                {dailyData.length > 0 ? (
                  <>
                    {dailyData.map((day, index) => (
                      <tr
                        key={index}
                        className={`${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        } border-b border-gray-100`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">
                              {day.date}
                            </span>
                            {renderPlanBadge(day.planType)}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium text-gray-900">
                            {formatIndianCurrency(day.sales)}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Incl GST {formatIndianCurrency(day.gst?.sales || 0)}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium text-gray-900">
                            {formatIndianCurrency(day.cost)}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Incl GST {formatIndianCurrency(day.gst?.cost || 0)}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium text-gray-900">
                            {formatIndianCurrency(day.grossProfit)}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Incl GST{" "}
                            {formatIndianCurrency(day.gst?.profit || 0)}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-bold text-emerald-600">
                            {formatIndianCurrency(day.netProfit)}
                          </div>
                        </td>
                      </tr>
                    ))}

                    {/* Totals Row */}
                    <tr className="bg-primary/10 border-t-2 border-primary">
                      <td className="py-3 px-4 text-sm font-bold text-gray-900">
                        TOTAL
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-bold text-gray-900">
                          {formatIndianCurrency(totals.totalSales)}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          GST: {formatIndianCurrency(totals.totalGSTSales)}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-bold text-gray-900">
                          {formatIndianCurrency(totals.totalCost)}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          GST: {formatIndianCurrency(totals.totalGSTCost)}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-bold text-gray-900">
                          {formatIndianCurrency(totals.totalGrossProfit)}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          GST: {formatIndianCurrency(totals.totalGSTProfit)}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-bold text-emerald-700">
                          {formatIndianCurrency(totals.totalNetProfit)}
                        </div>
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-8">
                      <div className="text-gray-500 text-sm">
                        No earnings data available
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Earnings are calculated daily at midnight
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info Card */}
        {/* <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 text-sm mb-2">
            How it works:
          </h4>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>• Daily earnings are calculated from your active deposits.</li>
            <li>• Cost = Capital ÷ 30 days</li>
            <li>
              • Prime (R) plan: 4% margin | Premium (P) plan: 8% margin
            </li>
            <li>• GST @ 5% is applied on both cost and sales.</li>
            <li>• Net Profit = Gross Profit - Profit GST</li>
          </ul>
        </div> */}

        {/* Refresh Button */}
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-4 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
        >
          <RefreshCw
            className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`}
          />
          {refreshing ? "Refreshing..." : "Refresh Data"}
        </button>
      </div>
    </MobileLayout>
  );
};

export default DailySalesReport;